/**
 * The Mon–Sun plan grid: Outlook meetings as grey read-only background,
 * focus blocks on top.
 *
 * Coordinates are a local date (column) and a Berlin wall-clock minute (row,
 * 48 px per hour, 15-minute snap) — see `lib/focusTime.ts` for the DST
 * handling. Gestures, all reported upward as callbacks (the page owns the
 * writes): pointer-draw on empty space creates a block for the armed goal,
 * a plain click opens the editor on that slot, dnd-kit moves a future block
 * (any day of the week), the bottom edge resizes a future or running block.
 * Past blocks are read-only; a click still opens the editor for the note.
 *
 * Below `md` the grid gives way to day chips and a tap-to-edit list.
 */
import { useCallback, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  DndContext,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from "@dnd-kit/core";
import { clsx } from "clsx";

import type { CalendarSyncState, FocusBlock, OutlookEvent } from "../../api/focus";
import {
  allDayCovers,
  blockTiming,
  isBusyEvent,
  layoutDayItems,
  overlapsBusy,
  type BlockTiming,
} from "../../lib/focus";
import {
  DAY_MINUTES,
  berlinDate,
  berlinOffsetMinutes,
  berlinToUtc,
  formatDayLabel,
  formatMinutes,
  hmFromMinutes,
  minuteOfDayBerlin,
  snapMinutes,
  spanOnDay,
  toBerlinIso,
} from "../../lib/focusTime";

/** Keep in step with `.week-col`'s background-size in index.css. */
const HOUR_PX = 48;
const MIN_PX = HOUR_PX / 60;
const SNAP = 15;
const SNAP_PX = SNAP * MIN_PX;
const DEFAULT_RANGE = { start: 7 * 60, end: 20 * 60 };
/** A click on empty space proposes a block this long. */
const CLICK_SLOT_MINUTES = 60;
const MIN_BLOCK_PX = 14;

interface PlacedBlock {
  block: FocusBlock;
  date: string;
  startMin: number;
  endMin: number;
  timing: BlockTiming;
  conflict: boolean;
}

interface PlacedEvent {
  event: OutlookEvent;
  startMin: number;
  endMin: number;
  col: number;
  cols: number;
}

interface DayModel {
  date: string;
  blocks: PlacedBlock[];
  events: PlacedEvent[];
  allDay: OutlookEvent[];
  plannedMinutes: number;
  isPast: boolean;
  isToday: boolean;
}

interface Range {
  start: number;
  end: number;
}

export interface WeekGridProps {
  days: string[];
  blocks: FocusBlock[];
  events: OutlookEvent[];
  nowMs: number;
  fullDay: boolean;
  /** The goal a drawn block is for; null = drawing opens the editor instead. */
  armedGoal: { id: string; title: string } | null;
  onDraw: (date: string, startMin: number, endMin: number) => void;
  onSlot: (date: string, startMin: number, endMin: number) => void;
  onMove: (block: FocusBlock, date: string, startMin: number, endMin: number) => void;
  onResize: (block: FocusBlock, endMin: number) => void;
  onOpen: (block: FocusBlock) => void;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);
const labelHm = (minutes: number) => (minutes >= DAY_MINUTES ? "24:00" : hmFromMinutes(minutes));

function buildDays(
  days: string[],
  blocks: FocusBlock[],
  events: OutlookEvent[],
  nowMs: number,
): DayModel[] {
  const today = berlinDate(nowMs);
  const timed = events.filter((e) => !e.all_day && e.starts_at && e.ends_at);
  return days.map((date) => {
    const placedBlocks: PlacedBlock[] = [];
    for (const block of blocks) {
      const span = spanOnDay(Date.parse(block.starts_at), Date.parse(block.ends_at), date);
      if (!span) continue;
      placedBlocks.push({
        block,
        date,
        ...span,
        timing: blockTiming(block, nowMs),
        conflict: block.status === "planned" && overlapsBusy(block, events),
      });
    }
    const spans = timed.flatMap((event) => {
      const span = spanOnDay(Date.parse(event.starts_at as string), Date.parse(event.ends_at as string), date);
      return span ? [{ event, ...span }] : [];
    });
    const layout = layoutDayItems(spans.map((s) => ({ id: s.event.id, startMin: s.startMin, endMin: s.endMin })));
    return {
      date,
      blocks: placedBlocks,
      events: spans.map((s) => ({ ...s, ...(layout.get(s.event.id) ?? { col: 0, cols: 1 }) })),
      allDay: events.filter((e) => e.all_day && allDayCovers(e, date)),
      plannedMinutes: placedBlocks
        .filter((b) => b.block.status === "planned")
        .reduce((sum, b) => sum + (b.endMin - b.startMin), 0),
      isPast: date < today,
      isToday: date === today,
    };
  });
}

/** 07–20 by default, widened to whole hours around anything planned outside it. */
function visibleRange(fullDay: boolean, model: DayModel[]): Range {
  if (fullDay) return { start: 0, end: DAY_MINUTES };
  let { start, end } = DEFAULT_RANGE;
  for (const day of model) {
    for (const item of [...day.blocks, ...day.events]) {
      start = Math.min(start, Math.floor(item.startMin / 60) * 60);
      end = Math.max(end, Math.ceil(item.endMin / 60) * 60);
    }
  }
  return { start, end };
}

/** "CET" / "CEST" for the week (Monday noon decides). */
function zoneLabel(date: string | undefined): string {
  if (!date) return "";
  return berlinOffsetMinutes(berlinToUtc(date, 12 * 60)) === 120 ? "CEST" : "CET";
}

const snapToGrid: Modifier = ({ transform }) => ({
  ...transform,
  y: Math.round(transform.y / SNAP_PX) * SNAP_PX,
});

interface Landing {
  /** The `blocks` prop the landing was made against — any new array retires it. */
  blocks: FocusBlock[];
  id: string;
  starts_at: string;
  ends_at: string;
}

export function WeekGrid(props: WeekGridProps) {
  const { days, blocks, events, nowMs, fullDay, onMove, onResize } = props;
  // Hold a dropped or resized block where it landed until the page's
  // optimistic write reaches `blocks`: the query cache notifies a task later,
  // and without this the block flashes back to its old slot for a frame.
  const [landing, setLanding] = useState<Landing | null>(null);
  // Retire it on the first new array (the optimistic write), so a rollback to
  // the original array can't revive the rejected position.
  if (landing && landing.blocks !== blocks) setLanding(null);
  const shown = useMemo(() => {
    if (!landing || landing.blocks !== blocks) return blocks;
    return blocks.map((b) =>
      b.id === landing.id ? { ...b, starts_at: landing.starts_at, ends_at: landing.ends_at } : b,
    );
  }, [blocks, landing]);

  const handleMove = useCallback(
    (block: FocusBlock, date: string, startMin: number, endMin: number) => {
      setLanding({
        blocks,
        id: block.id,
        starts_at: toBerlinIso(berlinToUtc(date, startMin)),
        ends_at: toBerlinIso(berlinToUtc(date, endMin)),
      });
      onMove(block, date, startMin, endMin);
    },
    [blocks, onMove],
  );
  const handleResize = useCallback(
    (block: FocusBlock, endMin: number) => {
      const date = berlinDate(Date.parse(block.starts_at));
      setLanding({ blocks, id: block.id, starts_at: block.starts_at, ends_at: toBerlinIso(berlinToUtc(date, endMin)) });
      onResize(block, endMin);
    },
    [blocks, onResize],
  );

  const model = useMemo(() => buildDays(days, shown, events, nowMs), [days, shown, events, nowMs]);
  const range = useMemo(() => visibleRange(fullDay, model), [fullDay, model]);
  return (
    <>
      <div className="hidden md:block">
        <DesktopWeek {...props} onMove={handleMove} onResize={handleResize} model={model} range={range} />
      </div>
      <div className="md:hidden week-mobile">
        <MobileWeek model={model} nowMs={nowMs} onOpen={props.onOpen} onSlot={props.onSlot} />
      </div>
    </>
  );
}

function DesktopWeek({
  model,
  range,
  nowMs,
  armedGoal,
  onDraw,
  onSlot,
  onMove,
  onResize,
  onOpen,
}: WeekGridProps & { model: DayModel[]; range: Range }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const placed = useMemo(
    () => new Map(model.flatMap((day) => day.blocks.map((b) => [b.block.id, b] as const))),
    [model],
  );
  // A drop ends on the dragged block itself, and the browser follows the
  // pointerup with a click there — which must not open the editor.
  const suppressClickUntil = useRef(0);
  const ignoreClick = useCallback(() => Date.now() < suppressClickUntil.current, []);

  function handleDragEnd(ev: DragEndEvent) {
    suppressClickUntil.current = Date.now() + 300;
    const item = placed.get(String(ev.active.id));
    const date = ev.over?.data.current?.date as string | undefined;
    if (!item || !date) return;
    const delta = Math.round(ev.delta.y / SNAP_PX) * SNAP;
    const duration = item.endMin - item.startMin;
    const start = clamp(item.startMin + delta, 0, DAY_MINUTES - duration);
    if (date === item.date && start === item.startMin) return;
    onMove(item.block, date, start, start + duration);
  }

  const hours: number[] = [];
  for (let m = range.start; m < range.end; m += 60) hours.push(m);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      modifiers={[snapToGrid]}
      onDragEnd={handleDragEnd}
    >
      <div className="week-grid">
        <div className="week-head">
          <div className="week-gutter-head">{zoneLabel(model[0]?.date)}</div>
          {model.map((day) => (
            <div
              key={day.date}
              className={clsx("week-day-head", day.isToday && "is-today", day.isPast && "is-past")}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="week-day-label">{formatDayLabel(day.date)}</span>
                <span className="week-day-planned">
                  {day.plannedMinutes ? formatMinutes(day.plannedMinutes) : "–"}
                </span>
              </div>
              {day.allDay.length > 0 ? (
                <div className="week-allday">
                  {day.allDay.map((e) => (
                    <span key={e.id} className="week-allday-chip" title={e.subject ?? undefined}>
                      {e.subject || "(no subject)"}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
        <div className="week-body" style={{ height: (range.end - range.start) * MIN_PX }}>
          <div className="week-gutter">
            {hours.map((m) => (
              <span key={m} className="week-hour" style={{ top: (m - range.start) * MIN_PX }}>
                {hmFromMinutes(m)}
              </span>
            ))}
          </div>
          {model.map((day) => (
            <DayColumn
              key={day.date}
              day={day}
              range={range}
              nowMs={nowMs}
              armedGoal={armedGoal}
              onDraw={onDraw}
              onSlot={onSlot}
              onOpen={onOpen}
              onResize={onResize}
              ignoreClick={ignoreClick}
            />
          ))}
        </div>
      </div>
    </DndContext>
  );
}

interface DraftSpan {
  anchor: number;
  current: number;
  startY: number;
  moved: boolean;
}

function draftSelection(d: DraftSpan): [number, number] {
  return d.current > d.anchor ? [d.anchor, d.current] : [d.current, d.anchor + SNAP];
}

function DayColumn({
  day,
  range,
  nowMs,
  armedGoal,
  onDraw,
  onSlot,
  onOpen,
  onResize,
  ignoreClick,
}: {
  day: DayModel;
  range: Range;
  nowMs: number;
  armedGoal: WeekGridProps["armedGoal"];
  onDraw: WeekGridProps["onDraw"];
  onSlot: WeekGridProps["onSlot"];
  onOpen: WeekGridProps["onOpen"];
  onResize: WeekGridProps["onResize"];
  ignoreClick: () => boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `day:${day.date}`, data: { date: day.date } });
  const colRef = useRef<HTMLDivElement | null>(null);
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      colRef.current = node;
      setNodeRef(node);
    },
    [setNodeRef],
  );
  const [draft, setDraft] = useState<DraftSpan | null>(null);

  function minuteAt(clientY: number): number {
    const top = colRef.current?.getBoundingClientRect().top ?? 0;
    return range.start + (clientY - top) / MIN_PX;
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0 || (e.target as HTMLElement).closest(".week-block")) return;
    const anchor = clamp(snapMinutes(minuteAt(e.clientY), "floor"), range.start, range.end - SNAP);
    if (berlinToUtc(day.date, anchor + SNAP) <= nowMs) return; // that slot is over
    e.currentTarget.setPointerCapture(e.pointerId);
    setDraft({ anchor, current: anchor + SNAP, startY: e.clientY, moved: false });
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!draft) return;
    const current = clamp(snapMinutes(minuteAt(e.clientY)), range.start, range.end);
    const moved = draft.moved || Math.abs(e.clientY - draft.startY) > 4;
    if (current !== draft.current || moved !== draft.moved) setDraft({ ...draft, current, moved });
  }

  function onPointerUp() {
    if (!draft) return;
    const [start, end] = draftSelection(draft);
    setDraft(null);
    if (!draft.moved) {
      onSlot(day.date, draft.anchor, Math.min(draft.anchor + CLICK_SLOT_MINUTES, DAY_MINUTES));
    } else if (armedGoal) {
      onDraw(day.date, start, end);
    } else {
      onSlot(day.date, start, end);
    }
  }

  const total = (range.end - range.start) * MIN_PX;
  const nowMinute = day.isToday ? minuteOfDayBerlin(nowMs) : null;
  const nowTop = nowMinute !== null ? (nowMinute - range.start) * MIN_PX : null;
  const ghost = draft?.moved ? draftSelection(draft) : null;

  return (
    <div
      ref={setRefs}
      className={clsx("week-col", day.isToday && "is-today", day.isPast && "is-past", isOver && "is-over")}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => setDraft(null)}
    >
      {nowTop !== null ? (
        <div className="week-past-shade" style={{ height: clamp(nowTop, 0, total) }} aria-hidden />
      ) : null}
      {day.events.map((item) => (
        <div
          key={item.event.id}
          className={clsx(
            "week-event",
            !isBusyEvent(item.event) && "is-free",
            item.event.foreign_focus && "is-foreign",
          )}
          style={{
            top: (item.startMin - range.start) * MIN_PX,
            height: Math.max((item.endMin - item.startMin) * MIN_PX, 10),
            left: `${(item.col / item.cols) * 100}%`,
            width: `${100 / item.cols}%`,
          }}
          title={`${item.event.subject || "(no subject)"} · ${labelHm(item.startMin)}–${labelHm(item.endMin)}${
            item.event.foreign_focus ? " · focus block from another plan" : ""
          }`}
        >
          <span className="week-event-title">{item.event.subject || "(no subject)"}</span>
        </div>
      ))}
      {day.blocks.map((item) => (
        <GridBlock
          key={item.block.id}
          item={item}
          range={range}
          onOpen={onOpen}
          onResize={onResize}
          ignoreClick={ignoreClick}
        />
      ))}
      {ghost ? (
        <div
          className="week-ghost"
          style={{ top: (ghost[0] - range.start) * MIN_PX, height: (ghost[1] - ghost[0]) * MIN_PX }}
        >
          <span>{armedGoal?.title ?? "New block"}</span>
          <span className="week-block-time">
            {labelHm(ghost[0])}–{labelHm(ghost[1])}
          </span>
        </div>
      ) : null}
      {nowTop !== null && nowTop >= 0 && nowTop <= total ? (
        <div className="week-now" style={{ top: nowTop }} aria-hidden />
      ) : null}
    </div>
  );
}

const SYNC: Record<CalendarSyncState, { glyph: string; label: string } | null> = {
  pending: { glyph: "○", label: "Outlook sync pending" },
  synced: { glyph: "·", label: "In Outlook" },
  error: { glyph: "!", label: "Outlook sync failed" },
  not_connected: null,
};

function SyncGlyph({ state, error }: { state: CalendarSyncState; error: string | null }) {
  const sync = SYNC[state];
  if (!sync) return null;
  return (
    <span
      className={clsx("week-sync", state === "error" && "is-error")}
      title={error ? `${sync.label}: ${error}` : sync.label}
    >
      {sync.glyph}
    </span>
  );
}

export function LockGlyph() {
  return (
    <svg className="week-lock" viewBox="0 0 10 12" role="img" aria-label="protected">
      <rect x="1" y="5" width="8" height="6.5" rx="1" fill="currentColor" />
      <path d="M3 5V3.5a2 2 0 0 1 4 0V5" fill="none" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

function GridBlock({
  item,
  range,
  onOpen,
  onResize,
  ignoreClick,
}: {
  item: PlacedBlock;
  range: Range;
  onOpen: (block: FocusBlock) => void;
  onResize: (block: FocusBlock, endMin: number) => void;
  ignoreClick: () => boolean;
}) {
  const { block, timing } = item;
  const removed = block.status !== "planned";
  const movable = !removed && timing === "future";
  const resizable = !removed && timing !== "past";
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: block.id,
    disabled: !movable,
  });
  const [resizeEnd, setResizeEnd] = useState<number | null>(null);
  const endMin = resizeEnd ?? item.endMin;
  const height = Math.max((endMin - item.startMin) * MIN_PX, MIN_BLOCK_PX);
  const compact = height < 34;

  const style: CSSProperties = {
    top: (item.startMin - range.start) * MIN_PX,
    height,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  };

  function onResizeStart(e: React.PointerEvent<HTMLSpanElement>) {
    e.stopPropagation();
    e.preventDefault();
    const handle = e.currentTarget;
    const startY = e.clientY;
    const base = item.endMin;
    let next = base;
    handle.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => {
      next = clamp(snapMinutes(base + (ev.clientY - startY) / MIN_PX), item.startMin + SNAP, DAY_MINUTES);
      setResizeEnd(next);
    };
    const done = (ev: PointerEvent) => {
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", done);
      handle.removeEventListener("pointercancel", done);
      setResizeEnd(null);
      if (ev.type === "pointerup" && next !== base) onResize(block, next);
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", done);
    handle.addEventListener("pointercancel", done);
  }

  const title = block.goal_title ?? "Untitled goal";
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(movable ? attributes : {})}
      {...(movable ? listeners : {})}
      role="button"
      tabIndex={0}
      aria-label={`${title}, ${labelHm(item.startMin)} to ${labelHm(endMin)}`}
      className={clsx(
        "week-block",
        block.protection === "protected" ? "week-block--protected" : "week-block--soft",
        item.conflict && "week-block--conflict",
        timing === "past" && "week-block--past",
        removed && "week-block--removed",
        isDragging && "week-block--dragging",
        movable && "is-movable",
        compact && "is-compact",
      )}
      title={
        item.conflict
          ? `${title} – overlaps a busy meeting`
          : block.goal_status && block.goal_status !== "active"
            ? `${title} – goal ${block.goal_status}`
            : title
      }
      onClick={() => {
        if (!ignoreClick()) onOpen(block);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(block);
        }
      }}
    >
      <div className="week-block-head">
        {block.protection === "protected" ? <LockGlyph /> : null}
        <span className="week-block-title">{title}</span>
        <SyncGlyph state={block.calendar.state} error={block.calendar.error} />
      </div>
      {!compact ? (
        <div className="week-block-time">
          {labelHm(item.startMin)}–{labelHm(endMin)}
          {removed ? " · removed in Outlook" : ""}
        </div>
      ) : null}
      {resizable ? (
        <span
          className="week-block-resize"
          onPointerDown={onResizeStart}
          onClick={(e) => e.stopPropagation()}
          aria-hidden
        />
      ) : null}
    </div>
  );
}

function MobileWeek({
  model,
  nowMs,
  onOpen,
  onSlot,
}: {
  model: DayModel[];
  nowMs: number;
  onOpen: (block: FocusBlock) => void;
  onSlot: (date: string, startMin: number, endMin: number) => void;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const selected =
    (picked && model.find((d) => d.date === picked)) ?? model.find((d) => d.isToday) ?? model[0];
  if (!selected) return null;

  type Row =
    | { kind: "block"; startMin: number; endMin: number; item: PlacedBlock }
    | { kind: "event"; startMin: number; endMin: number; event: OutlookEvent };
  const rows: Row[] = [
    ...selected.blocks.map((item): Row => ({ kind: "block", startMin: item.startMin, endMin: item.endMin, item })),
    ...selected.events.map(
      (e): Row => ({ kind: "event", startMin: e.startMin, endMin: e.endMin, event: e.event }),
    ),
  ].sort((a, b) => a.startMin - b.startMin);
  const addStart = selected.isToday
    ? Math.min(snapMinutes(minuteOfDayBerlin(nowMs), "ceil"), DAY_MINUTES - CLICK_SLOT_MINUTES)
    : 9 * 60;

  return (
    <div>
      <div className="week-daychips">
        {model.map((d) => (
          <button
            key={d.date}
            type="button"
            className={clsx("week-daychip", d.date === selected.date && "is-active", d.isToday && "is-today")}
            onClick={() => setPicked(d.date)}
          >
            <span>{formatDayLabel(d.date)}</span>
            <span className="week-daychip-planned">{d.plannedMinutes ? formatMinutes(d.plannedMinutes) : "–"}</span>
          </button>
        ))}
      </div>
      <ul className="agenda">
        {selected.allDay.map((e) => (
          <li key={e.id} className="agenda-row is-event">
            <span className="agenda-time">all day</span>
            <span className="agenda-title">{e.subject || "(no subject)"}</span>
          </li>
        ))}
        {rows.length === 0 && selected.allDay.length === 0 ? (
          <li className="agenda-row is-event">
            <span className="agenda-title">Nothing planned.</span>
          </li>
        ) : null}
        {rows.map((row) =>
          row.kind === "block" ? (
            <li
              key={`b:${row.item.block.id}`}
              className={clsx(
                "agenda-row is-block",
                row.item.block.protection === "protected" && "is-protected",
                row.item.timing === "past" && "is-past",
                row.item.conflict && "is-conflict",
              )}
            >
              <span className="agenda-time">
                {labelHm(row.startMin)}–{labelHm(row.endMin)}
              </span>
              <button type="button" className="agenda-title agenda-link" onClick={() => onOpen(row.item.block)}>
                {row.item.block.goal_title ?? "Untitled goal"}
              </button>
              <span className="agenda-meta">{row.item.block.protection}</span>
            </li>
          ) : (
            <li key={`e:${row.event.id}`} className="agenda-row is-event">
              <span className="agenda-time">
                {labelHm(row.startMin)}–{labelHm(row.endMin)}
              </span>
              <span className="agenda-title">{row.event.subject || "(no subject)"}</span>
              <span className="agenda-meta">{isBusyEvent(row.event) ? "meeting" : (row.event.show_as ?? "event")}</span>
            </li>
          ),
        )}
      </ul>
      {!selected.isPast ? (
        <button
          type="button"
          className="btn btn-sm btn-second mt-3"
          onClick={() => onSlot(selected.date, addStart, addStart + CLICK_SLOT_MINUTES)}
        >
          Add block
        </button>
      ) : null}
    </div>
  );
}
